import { doc, DocumentReference, DocumentSnapshot } from 'firebase/firestore';
import { db } from '../constants/firebaseModular';
import { auth } from '../constants/firebaseModular';
import { PatientDoc } from '@usacsmideo/types';
import { useFirestoreListener } from './useFirestoreListener';

// Fixes MIDEO-UNIFIED-N: The previous inline onSnapshot implementation called
// Sentry.captureMessage('Document does not exist.') whenever the snapshot didn't exist,
// which fired on every /login render (the old fallback patientId was '123').
// Now delegated to useFirestoreListener, which returns undefined for missing docs
// without reporting to Sentry. Callers must pass enabled=false when unauthenticated.
function usePatientDoc(patientId: string, { enabled = true }: { enabled?: boolean } = {}) {
  // Defense-in-depth: never start a listener for a patient that doesn't match the
  // authenticated user. The $patientId layout route should prevent this, but if a
  // child component somehow mounts during sign-out, this stops the listener cold.
  const uidMatch = auth.currentUser?.uid === patientId;

  // Use a placeholder ID when disabled to avoid Firestore's "even number of segments" error.
  // The listener won't subscribe when enabled=false, so the reference is never used.
  const safeId = patientId || '_placeholder';
  return useFirestoreListener<DocumentReference, PatientDoc>({
    target: doc(db, 'users', safeId),
    queryKey: ['patientDoc', patientId],
    label: 'patientDoc',
    enabled: enabled && uidMatch,
    transform: (snapshot: DocumentSnapshot) => {
      if (!snapshot.exists()) {
        return undefined;
      }
      return snapshot.data() as PatientDoc;
    },
  });
}

export default usePatientDoc;
